import React from 'react';
import { matchRoutes, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FuseUtils from '@/@fuse/utils';

/**
 * Route-level authorization in the LIVE router tree — the logic that used
 * to live in FuseAuthorization, which has been dead code since the React
 * Router data-router migration (51e9d6da: it sits as a child of
 * RouterProvider, which ignores children).
 *
 * Enforced per navigation, mirroring the old component:
 *   1. /billing needs the school's billing_enabled flag EXPLICITLY true
 *      plus the per-user Billing permission (same sources as the sidebar
 *      NavItem, so nav and routes can't disagree).
 *   1b. /billing/reports needs the Reports permission on top of that,
 *      because its data is served from behind the API's can:Reports gate.
 *   2. sub_admins only reach the permission-gated sections they hold
 *      (Students, Messaging, Live Streaming, ...).
 *   3. The matched route's `auth` roles must include the session role.
 *
 * Blocked visits bounce to "/" where RootRedirect sends every role home
 * (the old redirectRoute never handled product_owner); no-role sessions
 * go to /login. Public routes mount outside RootLayout and never pass
 * through here.
 */

// Sections a sub_admin may only enter with the matching permission —
// pathnames map to permission names (Live_Streaming → /livestreaming).
const SUB_ADMIN_GATED_PATHS = [
	'/students',
	'/staff',
	'/rooms',
	'/calendar',
	'/staff-schedule',
	'/messaging',
	'/livestreaming',
	'/reports',
	'/subadmin',
];

// '/Live_Streaming' (login) or 'Live_Streaming' (profile) → '/livestreaming'.
const toPathKey = (permission) =>
	`/${String(permission ?? '')
		.replace(/^\/+/, '')
		.split('_')
		.join('')
		.toLowerCase()}`;

const RouteAuthGuard = ({ routes, children }) => {
	const location = useLocation();
	const userRole = useSelector(({ auth }) => auth.user.role);
	const userPermission = useSelector(
		({ auth }) => auth.user.permissions ?? auth.user.data?.permissions
	);
	// Top-level school first — it's the school actually being viewed
	// (masquerade dialog + Auth.jsx boot refresh); data.school is the
	// owner's entry-time snapshot.
	const billingEnabled = useSelector(
		({ auth }) => (auth?.user?.school ?? auth?.user?.data?.school)?.billing_enabled
	);

	const { pathname } = location;
	const blocked = () => <Navigate replace to={!userRole || userRole.length === 0 ? '/login' : '/'} />;

	// Incident reports open from emails without a session (old exception).
	if (pathname.startsWith('/teacher/incident-report')) {
		return children;
	}

	// 1. Billing: feature flag + per-user permission.
	if (pathname.startsWith('/billing')) {
		const hasBillingPermission = userPermission
			?.map((permission) => permission?.replace(/^\/+/, ''))
			?.includes('Billing');
		if (!billingEnabled || (userPermission != null && !hasBillingPermission)) {
			return blocked();
		}
	}

	// 1b. The billing reports read /api/v2/reports/billing/* from behind the
	// API's can:Reports group, so the hub needs Reports on top of Billing.
	// `startsWith` covers the hub and all eight child report routes, which are
	// separate route entries. This runs before matchRoutes, exactly as the
	// /billing block above does, so it is indifferent to route-array shape -
	// and it is deliberately NOT an entry in SUB_ADMIN_GATED_PATHS, which
	// cannot express this path: toPathKey('Reports') is '/reports' and can
	// never equal '/billing/reports', and the list's split('-') filter is a
	// no-op on slash-nested paths.
	if (pathname.startsWith('/billing/reports')) {
		const hasReportsPermission = userPermission
			?.map((permission) => permission?.replace(/^\/+/, ''))
			?.includes('Reports');
		if (userPermission != null && !hasReportsPermission) {
			return blocked();
		}
	}

	// 2. sub_admin permission-gated sections: drop the routes for sections
	// the user lacks, so the path falls through to "no match" below.
	const heldPathKeys = userPermission?.map(toPathKey);
	const deniedPaths =
		userRole?.[0] === 'sub_admin'
			? SUB_ADMIN_GATED_PATHS.filter((path) => !heldPathKeys?.includes(path))
			: [];
	const allowedRoutes = routes.filter((route) => !deniedPaths.includes(route.path?.split('-')[0]));

	// 3. Role check on the matched route's auth config.
	const matched = matchRoutes(allowedRoutes, pathname)?.[0];
	if (!matched || matched.route.path === '*') {
		return blocked();
	}
	if (!FuseUtils.hasPermission(matched.route.auth, userRole)) {
		return blocked();
	}

	return children;
};

export default RouteAuthGuard;
